import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  // useMemo,
  useState,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  ButtonSelect,
  Icon,
  type IconKind,
  Loader,
  ModalIsland,
  TabGroup,
  TabItem,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import {
  ViolationsEntitlement,
  ViolationsRole,
  ViolationsTab,
  ViolationsTime,
  ViolationsUser,
} from '../../features';
import ExportBanner from '../../features/ViolationsReport/components/ExportBanner';
import ReportModal from '../../features/ViolationsReport/components/ReportModal';
import { violationsReportActions } from '../../features/ViolationsReport/data-store/violationsReportSlice';
import useExportStatusPolling from '../../features/ViolationsReport/hooks/use-exportStatusPolling';
import {
  AAG_SOD_ENTITLEMENT,
  AAG_SOD_ENTITLEMENT_REPORT,
  // AAG_SOD_ENTITLEMENT_REPORT,
  AAG_SOD_ROLE,
  AAG_SOD_TIME,
  AAG_SOD_USER,
  AAG_SOD_VIOLATIONS,
  CF_NEU_BLD,
  CF_SEC_BLD,
  testIds,
  VIOLATIONS_PAGE_TABS,
  violationsHome,
} from '../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../shared/hooks';
import { globalActions } from '../../shared/store/actions';

import './Violations.css';

/**
 * @typedef {Object} ViolationsProps
 * No props are required for this component
 */

/**
 * Violations component that handles the display and management of violation detection interface.
 * This component includes a header with navigation tabs and a content area for different violation views.
 *
 * @component
 * @description
 * The component manages different views for violations including:
 * - Violations Tab
 * - Time-based violations
 * - User-based violations
 * - Entitlement-based violations
 * - Role-based violations
 *
 * It also handles global loading states and error scenarios with a modal dialog.
 *
 * @example
 * return (
 *   <Violations />
 * )
 *
 * @returns {ReactElement} A React element representing the Violations interface
 */
function Violations(): ReactElement {
  const location = useLocation();
  const dispatch = useDispatch();

  // Violations report export status polling (scoped to Violations page only)
  useExportStatusPolling();
  const l = useInvertedLocalize();
  const [openReportModal, setOpenReportModal] = useState(false);

  const { navigateToUrl } = useNavigateTo();

  useEffect(() => {
    batch(() => {
      const activeTab = location.pathname || violationsHome;

      dispatch(globalActions.setGlobalError({ state: false, reason: '' }));
      dispatch(globalActions.setViolationsActiveTab(activeTab));

      // Reset selected report to default when navigating back to violations page
      if (activeTab === AAG_SOD_VIOLATIONS) {
        dispatch(violationsReportActions.resetSelectedReport());
      }
    });
  }, [location, dispatch]);

  const { activeViolationsTab, isGlobalLoading, dataTheme, isGlobalError } =
    useTypedSelector((state) => state.aag.global);
  const isSodDisabled = useTypedSelector(
    (state) => state.user.domainInfo?.SOD_MULTI_TENANT_DISABLED === 'true'
  );
  const violationsReportState = useTypedSelector((state) => state.aag.report);
  const entReportsState = useTypedSelector((state) => state.aag.entReports);

  const activeExports = useMemo(() => {
    if (activeViolationsTab === AAG_SOD_ENTITLEMENT) {
      return Array.isArray(entReportsState.activeExports)
        ? entReportsState.activeExports
        : [];
    }

    // For all other tabs (violations, time, user, role), only use violations report exports
    // Filter out any entitlement report types that might have leaked into violations state
    const violationsExports = Array.isArray(violationsReportState.activeExports)
      ? violationsReportState.activeExports
      : [];

    return violationsExports.filter((exportItem: { reportType: string }) => {
      const vioReportTypes = ['SUMMARY', 'DETAIL', 'PERMISSIONS'];

      return vioReportTypes.includes(exportItem.reportType);
    });
  }, [
    activeViolationsTab,
    entReportsState.activeExports,
    violationsReportState.activeExports,
  ]);

  const inProgressExportsCount = activeExports.length;

  // Generate tooltip text for running exports
  const runningExportsTooltip = useMemo(() => {
    if (activeExports.length === 0) {
      return '';
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const formattedTypes = activeExports.map(
      (exportItem: { reportType: string }) => {
        if (activeViolationsTab === AAG_SOD_ENTITLEMENT) {
          // Format entitlement report types for display
          if (
            exportItem.reportType === 'INHERENT_ENTITLEMENT_RISK' ||
            exportItem.reportType === 'INHERENT_ENTITLEMENT_USER'
          ) {
            return l('Entitlements with Inherent Risk');
          }

          return exportItem.reportType;
        }

        return (
          exportItem.reportType.charAt(0).toUpperCase() +
          exportItem.reportType.slice(1).toLowerCase()
        );
      }
    );

    const formatTypesList = (types: string[]): string => {
      // Remove duplicates
      const uniqueTypes = [...new Set(types)];

      if (uniqueTypes.length === 1) {
        return uniqueTypes[0];
      }

      if (uniqueTypes.length === 2) {
        return `${uniqueTypes[0]} and ${uniqueTypes[1]}`;
      }

      const lastType = uniqueTypes[uniqueTypes.length - 1];
      const allButLast = uniqueTypes.slice(0, -1);

      return `${allButLast.join(', ')} and ${lastType}`;
    };

    if (activeViolationsTab === AAG_SOD_ENTITLEMENT) {
      return `${l('An export is already in progress for ')} ${formatTypesList(
        formattedTypes
      )}`;
    }

    return `Export with report type ${formatTypesList(
      formattedTypes
    )} is in progress.`;
  }, [activeExports, activeViolationsTab, l]);

  /**
   * Handles tab change events in the violations interface
   *
   * @param {string} value - The identifier of the selected tab
   * @returns {void}
   */
  const handleViolationsTabChange = useCallback(
    (value: string): void => {
      if (!isGlobalLoading && activeViolationsTab !== value) {
        navigateToUrl(value);
      }
    },
    [navigateToUrl, isGlobalLoading, activeViolationsTab]
  );

  /**
   * Handles page refresh when errors occur
   *
   * @returns {void}
   */
  const onRefreshClick = () => {
    window.location.reload();
  };

  /**
   * Navigates back to the request home page
   *
   * @returns {void}
   */
  const onBackClick = () => {
    window.location.replace('/ECMv6/request/requestHome');
  };

  if (isSodDisabled) {
    return (
      <div
        data-theme={dataTheme}
        className='Sod-Violations'
        data-testid={testIds.SOD_VIOLATIONS_PAGE}>
        <ModalIsland
          kind='informational'
          alertTitle={l('SOD Violations Workbench Disabled')}
          alertSubtitle={l(
            'The SOD Violations Workbench is currently disabled. Please reach out to your administrator or raise a support ticket to enable this service.'
          )}
          onClose={() => {}}
          isCloseButtonVisible={false}
          isOpen
          primaryButton={
            <Button
              type='button'
              kind='filled'
              isEnabled
              size='medium'
              onClick={onBackClick}>
              {l('Go Back')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div
      data-theme={dataTheme}
      className='Sod-Violations'
      data-testid={testIds.SOD_VIOLATIONS_PAGE}>
      <header
        className='Sod-Violations-header'
        data-testid={testIds.SOD_VIOLATIONS_PAGE_HEADER}>
        {/* Title */}
        <Box className='Sod-Violations-headerTitleWrapper'>
          <Typography kind='h1' color={CF_NEU_BLD}>
            {l('Violations Workbench')}
          </Typography>
        </Box>
        {/* TabGroup */}
        <Box className='Sod-Violations-headerTabGroupWrapper'>
          <TabGroup
            value={activeViolationsTab}
            onChange={handleViolationsTabChange}
            className='Sod-Violations-headerTabGroup'>
            {VIOLATIONS_PAGE_TABS.filter((page) => page.isActive).map(
              ({ label, value, icon }) => (
                <TabItem
                  key={value}
                  label={l(label)}
                  value={value}
                  icon={<Icon kind={icon as IconKind} color={CF_SEC_BLD} />} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
                />
              )
            )}
          </TabGroup>
        </Box>
        {/* Export Report */}
        <Box className='Sod-Violations-exportWrapper'>
          {(() => {
            const exportButton = (
              <ButtonSelect
                kind='icon'
                onClick={() => {
                  if (activeViolationsTab === AAG_SOD_ENTITLEMENT) {
                    navigateToUrl(AAG_SOD_ENTITLEMENT_REPORT);
                  } else {
                    setOpenReportModal(true);
                  }
                }}
                isOpen={false}
                prefixIcon={<Icon kind='Report' />}
                isDisabled={isGlobalLoading}
                BadgeComp={
                  inProgressExportsCount > 0 ? (
                    <Badge
                      kind='number'
                      color='primary'
                      size='small'
                      number={inProgressExportsCount}
                    />
                  ) : undefined
                }
                dataTestId={testIds.SOD_VIOLATIONS_PAGE_HEADER_EXP_BTN}
              />
            );

            return runningExportsTooltip ? (
              <Tooltip text={runningExportsTooltip} placement='left'>
                {exportButton}
              </Tooltip>
            ) : (
              exportButton
            );
          })()}
          <ExportBanner className='Sod-Violations-exportBanner' />
          {openReportModal && (
            <ReportModal
              isOpen={openReportModal}
              setIsOpen={setOpenReportModal}
            />
          )}
        </Box>
      </header>
      <Box className='Sod-Violations-scrollArea'>
        {isGlobalLoading && (
          <Box className='Sod-Violations-loaderWrapper'>
            <Loader kind='loop' format='main' color='brand' />
          </Box>
        )}
        <Switch>
          <Route path={AAG_SOD_VIOLATIONS} component={ViolationsTab} />
          <Route path={AAG_SOD_TIME} component={ViolationsTime} />
          <Route path={AAG_SOD_USER} component={ViolationsUser} />
          <Route path={AAG_SOD_ENTITLEMENT} component={ViolationsEntitlement} />
          <Route path={AAG_SOD_ROLE} component={ViolationsRole} />
        </Switch>
      </Box>
      <ModalIsland
        kind='critical'
        alertTitle={l('Could not Load Page')}
        alertSubtitle={
          activeViolationsTab.includes('/violations')
            ? l(
                'This page of the violations did not load properly due to a network issue. Please refresh or try again later.'
              )
            : l(
                'This page of the entitlements did not load properly due to a network issue. Please refresh or try again later.'
              )
        }
        onClose={() => {}}
        isCloseButtonVisible={false}
        isOpen={isGlobalError}
        primaryButton={
          <Button
            type='button'
            kind='filled'
            isEnabled
            size='medium'
            onClick={onRefreshClick}>
            {l('Refresh')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={onBackClick}>
            {l('Go Back')}
          </Button>
        }
        className='Sod-ViolationsErrorModal'
      />
    </div>
  );
}

export default memo(Violations);
